import { useState } from 'react';
import type { Publication, ReelDetail, RenderQuality } from '@reelmachine/core';
import { useOverrideQuality, useResolvePublication } from '../api/hooks';
import { Icon } from '../ui/Icon';
import { useToast } from '../ui/Toast';

export const OVERRIDE_MIN = 10;
export const NOTE_MIN = 5;

// ---------- quality (review F04/F06) ----------

export type QualityView = 'checking' | 'passed' | 'review' | 'failed' | 'unchecked';

export const QUALITY_LABEL: Record<QualityView, string> = {
  checking: 'Checking…',
  passed: 'Passed',
  review: 'Needs review',
  failed: 'Failed',
  unchecked: 'Could not be checked',
};

/**
 * What the checks mean for the person. "review" is only reviewable video warnings; a factual failure or a
 * hard video failure is "failed" and can never be accepted.
 */
export function qualityView(q: RenderQuality | undefined): QualityView {
  if (!q) return 'unchecked';
  switch (q.status) {
    case 'pending':
      return 'checking';
    case 'passed':
      return 'passed';
    case 'unchecked':
      return 'unchecked';
    case 'failed':
      return canOverride(q) ? 'review' : 'failed';
  }
}

/** Only video warnings, with facts checked and passed, and not accepted yet. */
export function canOverride(q: RenderQuality | undefined): boolean {
  return (
    !!q &&
    q.status === 'failed' &&
    q.fidelity?.passed === true &&
    (q.fidelity.missing?.length ?? 0) === 0 &&
    !!q.video &&
    (q.video.hardFailures?.length ?? 0) === 0 &&
    (q.video.warnings?.length ?? 0) > 0 &&
    !q.override
  );
}

function chipClass(v: QualityView): string {
  if (v === 'passed') return 'chip ok';
  if (v === 'checking') return 'chip rec';
  return 'chip warn';
}

const CHECK_NAME: Record<string, string> = {
  speech: 'speech',
  overlays: 'text on screen',
  endCard: 'end card',
  caption: 'caption',
};

function OverrideForm({ reelId, onDone }: { reelId: string; onDone: () => void }) {
  const toast = useToast();
  const override = useOverrideQuality(reelId);
  const [reason, setReason] = useState('');
  const short = reason.trim().length < OVERRIDE_MIN;
  return (
    <form
      className="confirm"
      aria-label="Accept video warnings"
      onSubmit={(e) => {
        e.preventDefault();
        if (short) return;
        override.mutate(reason.trim(), {
          onSuccess: () => {
            toast('Warnings accepted');
            onDone();
          },
          onError: (err) => toast(err.message),
        });
      }}
    >
      <p className="small">Only video warnings. Facts are never overridden. Your reason is saved with the reel.</p>
      <div className="field">
        <label className="field-l" htmlFor="override-reason">
          Why is this fine to post?
        </label>
        <textarea
          id="override-reason"
          rows={3}
          maxLength={1000}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        <span className="hint">At least {OVERRIDE_MIN} characters.</span>
      </div>
      <div className="row nowrap">
        <button type="button" className="btn sec" onClick={onDone}>
          Cancel
        </button>
        <button type="submit" className="btn" disabled={override.isPending || short}>
          Accept warnings
        </button>
      </div>
    </form>
  );
}

/** Status of the checks on the finished video, with "Run checks again" and, for staff, accepting warnings. */
export function QualityCard({
  detail,
  staff,
  locked,
  recheck,
  rechecking,
}: {
  detail: ReelDetail;
  staff: boolean;
  /** A publication is open or done: nothing to change here. */
  locked: boolean;
  recheck: () => void;
  rechecking: boolean;
}) {
  const [overriding, setOverriding] = useState(false);
  const render = detail.reel.render;
  if (!render) return null;
  const q = render.quality;
  const view = qualityView(q);
  // The list of reasons offers "Run checks again" when a recheck is what is missing; never show it twice.
  const listOffersRecheck = !!detail.eligibility?.reasons.some((r) => actionFor(r.code) === 'recheck');
  const problems = [
    ...(q?.fidelity?.claimViolations ?? []).map((t) => ({ t, kind: 'fact' as const })),
    ...(q?.video?.hardFailures ?? []).map((t) => ({ t, kind: 'video' as const })),
    ...(q?.video?.warnings ?? []).map((t) => ({ t, kind: 'warning' as const })),
  ];
  const missing = q?.fidelity?.missing ?? [];
  const factsFailed = q?.fidelity?.passed === false && missing.length === 0;

  return (
    <section className={`card ${view === 'passed' || view === 'checking' ? '' : 'warnc'}`} aria-label="Video checks">
      <div className="row between nowrap">
        <span className="label">Video checks</span>
        <span className={chipClass(view)} role="status">
          {view === 'passed' && <Icon name="check" />}
          {QUALITY_LABEL[view]}
        </span>
      </div>
      {view === 'checking' && <p className="small">Checking speech, text on screen and caption against the source.</p>}
      {view === 'unchecked' && (
        <p className="small">
          {missing.length
            ? `Could not check the ${missing.map((m) => CHECK_NAME[m] ?? m).join(', ')}.`
            : 'This video has no checks.'}{' '}
          It cannot be posted until the checks run.
        </p>
      )}
      {problems.length > 0 && (
        <ul className="qa">
          {problems.map((p, i) => (
            <li key={i}>
              <Icon name="alert" />
              <span>
                {p.kind === 'fact' ? 'Fact: ' : p.kind === 'warning' ? 'Warning: ' : ''}
                {p.t}
              </span>
            </li>
          ))}
        </ul>
      )}
      {factsFailed && (
        <p className="small">Fix the script and make the video again. Factual problems cannot be accepted.</p>
      )}
      {q?.override && <p className="small muted">Warnings accepted by staff: {q.override.reason}</p>}
      {!locked && view === 'review' && !staff && (
        <p className="small">Make the video again, or ask BeatSquares to review the warnings.</p>
      )}
      {!locked && view === 'review' && staff && !overriding && (
        <button type="button" className="btn sec" onClick={() => setOverriding(true)}>
          Accept warnings
        </button>
      )}
      {!locked && overriding && view === 'review' && (
        <OverrideForm reelId={detail.reel.id} onDone={() => setOverriding(false)} />
      )}
      {!locked && view !== 'checking' && view !== 'passed' && !listOffersRecheck && (
        <button type="button" className="btn sec" disabled={rechecking} onClick={recheck}>
          Run checks again
        </button>
      )}
    </section>
  );
}

// ---------- eligibility (review F04) ----------

export type ReasonAction = 'makeVideo' | 'remake' | 'recheck' | 'caption' | 'script';

export const ACTION_LABEL: Record<ReasonAction, string> = {
  makeVideo: 'Make video',
  remake: 'Make video again',
  recheck: 'Run checks again',
  caption: 'Write caption',
  script: 'Edit script',
};

/** The one action that fixes a reason, if the person can take one. */
export function actionFor(code: string): ReasonAction | undefined {
  switch (code) {
    case 'no_render':
      return 'makeVideo';
    case 'render_stale':
    case 'quality_failed_video':
      return 'remake';
    case 'quality_unchecked':
    case 'assessment_mismatch':
      return 'recheck';
    case 'quality_failed_facts':
      return 'script';
    case 'no_caption':
      return 'caption';
    default:
      return undefined;
  }
}

/** Why the reel cannot be posted yet: one line per reason, each with its action (shown once). */
export function EligibilityList({
  eligibility,
  onAction,
  busy,
}: {
  eligibility: NonNullable<ReelDetail['eligibility']>;
  onAction: (a: ReasonAction) => void;
  busy: boolean;
}) {
  const reasons = eligibility.reasons.filter((r) => r.code !== 'publication_open');
  if (eligibility.ok || reasons.length === 0) return null;
  const shown = new Set<ReasonAction>();
  return (
    <section className="card" aria-label="Before you can post">
      <span className="label">Before you can post</span>
      <ul className="plain-list reasons">
        {reasons.map((r, i) => {
          const a = actionFor(r.code);
          const show = a && !shown.has(a);
          if (a) shown.add(a);
          return (
            <li key={`${r.code}-${i}`}>
              <span>{r.message}</span>
              {show && (
                <button type="button" className="iconbtn" disabled={busy} onClick={() => onAction(a)}>
                  {ACTION_LABEL[a]}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// ---------- publication (review F03) ----------

export type PublicationView = 'posting' | 'checking' | 'published' | 'rejected';

export function publicationView(
  p: Publication | undefined,
  reel: { stage: string; post?: { url?: string } | undefined },
): PublicationView | undefined {
  if (p) {
    switch (p.state) {
      case 'intent':
      case 'submitting':
        return 'posting';
      case 'accepted':
      case 'unknown':
        return 'checking';
      case 'published':
        return 'published';
      case 'rejected':
        return 'rejected';
      case 'cancelled':
        return undefined;
    }
  }
  return reel.stage === 'stats' && reel.post ? 'published' : undefined;
}

export const PUBLICATION_LABEL: Record<PublicationView, string> = {
  posting: 'Posting…',
  checking: 'Checking with Instagram…',
  published: 'Published',
  rejected: 'Not posted',
};

function ResolveForm({ reelId }: { reelId: string }) {
  const toast = useToast();
  const resolve = useResolvePublication(reelId);
  const [outcome, setOutcome] = useState<'published' | 'not_published' | null>(null);
  const [url, setUrl] = useState('');
  const [note, setNote] = useState('');
  const urlOk = /^https:\/\/\S+$/.test(url.trim());
  const ready = outcome !== null && note.trim().length >= NOTE_MIN && (outcome === 'not_published' || urlOk);
  return (
    <form
      className="confirm"
      aria-label="Resolve publication"
      onSubmit={(e) => {
        e.preventDefault();
        if (!ready || !outcome) return;
        resolve.mutate(
          { outcome, note: note.trim(), ...(outcome === 'published' ? { url: url.trim() } : {}) },
          {
            onSuccess: () => toast('Saved'),
            onError: (err) => toast(err.message),
          },
        );
      }}
    >
      <p className="small">Staff: check the Instagram account by hand, then record what you found.</p>
      <fieldset className="choices">
        <legend className="field-l">Is the reel on Instagram?</legend>
        <label className="row nowrap">
          <input
            type="radio"
            name="resolve-outcome"
            checked={outcome === 'published'}
            onChange={() => setOutcome('published')}
          />
          Yes, it is live
        </label>
        <label className="row nowrap">
          <input
            type="radio"
            name="resolve-outcome"
            checked={outcome === 'not_published'}
            onChange={() => setOutcome('not_published')}
          />
          No, it is not there
        </label>
      </fieldset>
      {outcome === 'published' && (
        <div className="field">
          <label className="field-l" htmlFor="resolve-url">
            Link to the post
          </label>
          <input
            id="resolve-url"
            type="url"
            inputMode="url"
            placeholder="https://www.instagram.com/reel/…"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
        </div>
      )}
      <div className="field">
        <label className="field-l" htmlFor="resolve-note">
          Note
        </label>
        <input id="resolve-note" type="text" maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
        <span className="hint">What did you check? At least {NOTE_MIN} characters.</span>
      </div>
      <button type="submit" className="btn" disabled={resolve.isPending || !ready}>
        Save
      </button>
    </form>
  );
}

/** Where the publication is: posting, checking with Instagram, published with link, or not posted with reason. */
export function PublicationCard({ detail, staff }: { detail: ReelDetail; staff: boolean }) {
  const { reel } = detail;
  const p = reel.publication;
  const view = publicationView(p, reel);
  if (!view) return null;
  const url = p?.url ?? reel.post?.url;
  return (
    <section className={`card ${view === 'rejected' ? 'warnc' : ''}`} aria-label="Instagram post">
      <div className="row between nowrap">
        <h3 role="status">{PUBLICATION_LABEL[view]}</h3>
        {view === 'published' && (
          <span className="chip ok">
            <Icon name="check" /> Live
          </span>
        )}
      </div>
      {view === 'posting' && <p className="small">Sending the video to Instagram. You can leave this page.</p>}
      {view === 'checking' && (
        <p className="small">
          {p?.state === 'unknown'
            ? 'Instagram has not confirmed the post yet. We are checking. Do not post it again.'
            : 'Instagram has the video and is processing it.'}
        </p>
      )}
      {view === 'published' && url && (
        <a className="iconbtn open-link" href={url} target="_blank" rel="noreferrer">
          Open on Instagram
        </a>
      )}
      {view === 'rejected' && <p className="small">{p?.error ?? 'Instagram did not accept the post.'}</p>}
      {p?.resolution && <p className="small muted">Checked by hand: {p.resolution.note}</p>}
      {view === 'checking' && p?.state === 'unknown' && staff && <ResolveForm reelId={reel.id} />}
    </section>
  );
}
